import { AppointmentListItem } from './../shared-components/Dsi/types/EnrollResponse';
import { apiRoutes } from '@/app/apiConfig/apiRoutes';
import {
  Address,
  CreateClientAndOrderRequestPayload,
  CreateOrderResponseResult,
  EsiId,
  OrdersResult,
  GetAddressCandidatesRequest,
  SubmitAddressCandidateResponse,
  UrgentStatus,
} from '../shared-components/Types/Order';
import { Ticket } from '../shared-components/Types/Ticket';
import { Order } from 'app/shared-components/Types/Order';
import { CreateQuoteRequest, Quote, QuoteDetails } from 'app/shared-components/Types/Quote';
import { Paged } from 'app/shared-components/Types/Paged';
import { apiSlice } from './apiSlice';
import { Activity, AddActivityDto, EditActivity } from '../shared-components/Types/Activity';
import { Schema as NoteSchema } from '../shared-components/Forms/Actions/Leads/AddNote/config';
import { Schema as TicketSchema } from '../shared-components/Forms/Actions/Leads/CreateTicket/config';
import { Schema as VaultSchema } from '../shared-components/Forms/Vault/config';
import { Schema as DsiVaultSchema } from '../shared-components/Dsi/schemas/dsiQuestionsForm';
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from 'material-react-table';
import { createMyUtiliesURL } from '@/utils/helpers';
import { Subscription } from '../shared-components/Types/Subscription';
import { CancellationType } from '../shared-components/Types/CancellationReson';
import { InsurancePayload } from 'app/shared-components/Types/Insurance';
import { AdditionalFilter } from '../hooks/useFilterPagination';
import { OrderSubmitResponse } from '../shared-components/Dsi/types/OrderSubmitResponse';
import { CreditCheckResponse } from '../shared-components/Dsi/types/CreditCheckResponse';
import { CreateOrderResponse } from '../shared-components/Dsi/types/CreateOrderResponse';
import { EnrollResponse } from '../shared-components/Dsi/types/EnrollResponse';
import { RcResponse } from '../shared-components/Dsi/types/RCPayment';
import { DsiInfo } from '../main/order-details/DsiType';
import { SubmitAddressCandidateRequest } from '../main/order-details/components/AddressSelection';
import { AddressCandidate, SetAddressCandidate } from '../shared-components/Types/AddressCandidate';

export const ordersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => {
    return {
      // orders
      getOrders: builder.query<
        OrdersResult,
        {
          filters: MRT_ColumnFiltersState;
          sorting: MRT_SortingState;
          pagination: MRT_PaginationState;
          calculateTotals: boolean;
          extraFilters?: AdditionalFilter[];
        }
      >({
        query: ({ filters, sorting, pagination, calculateTotals, extraFilters }) => {
          let filterData = [...filters];

          if (window.location.pathname === '/leads') {
            filterData = [{ id: 'stageId', value: '1' }, ...filters];
          }

          if (window.location.pathname === '/processing') {
            filterData = [{ id: 'stageId', value: '3' }, ...filters];
          }

          const urgentFilter = filters.find((filter) => filter.id === 'isUrgent');
          if (urgentFilter) {
            filterData = [
              ...filterData.filter((filter) => filter.id !== urgentFilter.id),
              {
                id: urgentFilter.id,
                value: urgentFilter.value == UrgentStatus.Urgent,
              },
            ];
          }

          const urlParams = createMyUtiliesURL(filterData, sorting);
          const extraFiltersParams = new URLSearchParams();
          extraFilters?.forEach((filter) => {
            extraFiltersParams.append(filter.name, filter.value as string);
          });

          return {
            url: apiRoutes.orders,
            method: 'GET',
            params: {
              ...Object.fromEntries(urlParams),
              ...Object.fromEntries(extraFiltersParams),
              page: pagination.pageIndex + 1,
              pageSize: pagination.pageSize,
              calculateTotals,
            },
          };
        },
        providesTags: ['Orders'],
      }),
      getLeadsOrders: builder.query<
        OrdersResult,
        {
          filters: MRT_ColumnFiltersState;
          sorting: MRT_SortingState;
          pagination: MRT_PaginationState;
          calculateTotals: boolean;
        }
      >({
        query: ({ filters, sorting, pagination, calculateTotals }) => {
          const urlParams = createMyUtiliesURL(filters, sorting);

          return {
            url: apiRoutes.leads,
            method: 'GET',
            params: {
              ...Object.fromEntries(urlParams),
              page: pagination.pageIndex + 1,
              pageSize: pagination.pageSize,
              calculateTotals,
            },
          };
        },
        providesTags: ['Orders'],
      }),
      getOrder: builder.query<Order, string | number | undefined>({
        query: (orderId) => {
          if (orderId) {
            return { url: `${apiRoutes.orders}/${orderId}`, method: 'GET' };
          }
          throw new Error('Order id is not defined');
        },
        providesTags: (_result, _error, orderId) => [{ type: 'Order', id: orderId }],
      }),
      createOrderAndClient: builder.mutation<
        CreateOrderResponseResult,
        CreateClientAndOrderRequestPayload
      >({
        query: ({ ...patch }) => {
          return {
            url: '/clients/order',
            method: 'POST',
            data: {
              clientData: patch.clientData,
              orderData: patch.orderData,
            },
          };
        },
        transformResponse: (_response: CreateOrderResponseResult, meta: string) => {
          return { id: meta };
        },
        invalidatesTags: ['Order', 'Orders', 'Clients'],
      }),
      updateOrder: builder.mutation<
        CreateOrderResponseResult,
        { orderId: string | number | undefined; order: any }
      >({
        query: ({ orderId, order }) => {
          if (orderId) {
            return {
              url: `${apiRoutes.orders}/${orderId}`,
              method: 'PUT',
              data: {
                ...order,
              },
            };
          }
          throw new Error('Order id is not defined');
        },
        transformResponse: (_result: Order) => {
          return { id: _result.id.toString() };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error
            ? []
            : [
                { type: 'Order', id: orderId },
                { type: 'Activity', id: orderId },
                'Orders',
                'Clients',
                'Subscriptions',
                'DepositItems',
              ],
      }),
      updateQuoteStep: builder.mutation<void, { quoteId: number; step: string; orderId: number }>({
        query: ({ quoteId, step }) => ({
          url: `${apiRoutes.dsi}/SetStep/${quoteId}`,
          method: 'PATCH',
          data: { step },
        }),
        invalidatesTags: (_result, _error, { orderId }) => [
          { type: 'Order', id: orderId },
          { type: 'Quotes', id: orderId },
        ],
      }),
      // tickets
      getOrderTickets: builder.query<
        Paged<Ticket>,
        {
          id: string | undefined;
          filters: MRT_ColumnFiltersState;
          sorting: MRT_SortingState;
          pagination: MRT_PaginationState;
        }
      >({
        query: ({ id, filters, sorting, pagination }) => {
          if (id) {
            const urlParams = createMyUtiliesURL(filters, sorting);

            return {
              url: `${apiRoutes.orders}/${id}/tickets`,
              method: 'GET',
              params: {
                page: pagination.pageIndex + 1,
                pageSize: pagination.pageSize,
                ...Object.fromEntries(urlParams),
              },
            };
          }
          throw new Error('Order id is not defined');
        },
        providesTags: (_result, _error, { id }) => [{ type: 'Tickets', id: id }],
      }),
      createTicket: builder.mutation<Ticket, { orderId?: number; ticket: TicketSchema }>({
        query: ({ orderId, ticket }) => {
          if (orderId) {
            return {
              url: `${apiRoutes.orders}/${orderId}/tickets`,
              method: 'POST',
              data: ticket,
            };
          }
          throw new Error('Order Id is not defined');
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : [{ type: 'Tickets', id: orderId }, 'Tickets'],
      }),
      updateTicket: builder.mutation<
        Ticket,
        { orderId?: number; ticket: TicketSchema; ticketId: number }
      >({
        query: ({ orderId, ticket, ticketId }) => {
          if (orderId && ticketId) {
            return {
              url: `${apiRoutes.orders}/${orderId}/tickets/${ticketId}`,
              method: 'PUT',
              data: ticket,
            };
          }
          throw new Error('Order id or Ticket id are not defined');
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : [{ type: 'Tickets', id: orderId }, 'Tickets'],
      }),
      getOrderAllQuotes: builder.query<Paged<Quote>, number>({
        query: (id) => {
          return {
            url: `${apiRoutes.orders}/${id}/quotes`,
            method: 'GET',
          };
        },
        providesTags: (_result, _error, id) => [{ type: 'Quotes', id: id }],
      }),
      addOrderQuote: builder.mutation<any, { orderId: string; quote: CreateQuoteRequest }>({
        query: ({ orderId, quote }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/quotes`,
            method: 'POST',
            data: quote,
          };
        },
        transformResponse: (_response: any, meta: string) => {
          return { id: meta };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error
            ? []
            : [
                { type: 'Quotes', id: orderId },
                // 'Quotes',
              ],
      }),
      removeOrderQuote: builder.mutation<Quote, { orderId: number; quoteId: number }>({
        query: ({ orderId, quoteId }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/quotes/${quoteId}`,
            method: 'DELETE',
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error
            ? []
            : [
                { type: 'Quotes', id: orderId },
                // 'Quotes',
              ],
      }),
      updateOrderQuote: builder.mutation<Quote, { orderId: number; quote: Quote }>({
        query: ({ orderId, quote }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/quotes/${quote.id}`,
            method: 'PUT',
            data: quote,
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error
            ? []
            : [
                { type: 'Quotes', id: orderId },
                // 'Quotes',
              ],
      }),
      // subscriptions
      getOrderSubscriptions: builder.query<
        Paged<Subscription>,
        {
          id: string | undefined;
          filters: MRT_ColumnFiltersState;
          sorting: MRT_SortingState;
          pagination: MRT_PaginationState;
        }
      >({
        query: ({ id, filters, sorting, pagination }) => {
          const urlParams = createMyUtiliesURL(filters, sorting);

          return {
            url: `${apiRoutes.orders}/${id}/subscriptions`,
            method: 'GET',
            params: {
              page: pagination.pageIndex + 1,
              pageSize: pagination.pageSize,
              ...Object.fromEntries(urlParams),
            },
          };
        },
        providesTags: (_result, _error, { id }) => [{ type: 'Subscriptions', id }],
      }),
      addOrderSubscription: builder.mutation<
        { id: string },
        { orderId: number | string; quoteId: number; quote: Quote }
      >({
        query: ({ orderId, quoteId, quote }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/quotes/${quoteId}/subscriptions`,
            method: 'POST',
            data: quote,
          };
        },
        transformResponse: (_d: any, meta: string) => {
          return { id: meta };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error
            ? []
            : [
                { type: 'Subscriptions', id: orderId },
                { type: 'Subscriptions' },
                // 'Quotes',
              ],
      }),
      deleteOrderSubscription: builder.mutation<
        Subscription,
        { orderId: number; subscriptionId: number }
      >({
        query: ({ orderId, subscriptionId }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/subscriptions/${subscriptionId}`,
            method: 'DELETE',
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error
            ? []
            : [
                { type: 'Subscriptions', id: orderId },
                { type: 'Subscriptions' },
                // 'Quotes',
              ],
      }),
      updateOrderSubscription: builder.mutation<
        Subscription,
        { orderId: number; quoteId: number; subscriptionId: number; data: Subscription }
      >({
        query: ({ orderId, quoteId, subscriptionId, data }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/quotes/${quoteId}/subscriptions/${subscriptionId}`,
            method: 'PUT',
            data,
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error
            ? []
            : [
                { type: 'Subscriptions', id: orderId },
                { type: 'Subscriptions' },
                { type: 'ClientSubscriptions' },
              ],
      }),
      // stages
      updateStage: builder.mutation<Order, { orderId: string; stageId: number }>({
        query: ({ orderId, stageId }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/changestage/${stageId}`,
            method: 'PUT',
          };
        },

        async onQueryStarted({ orderId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              ordersApiSlice.util.updateQueryData('getOrder', orderId, (order: Order) => {
                if (order) {
                  order.stage = { ...data.stage };
                }
                return order;
              }),
            );
          } catch (e) {
            console.log('unable to update stage', e);
          }
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error
            ? []
            : [
                { type: 'Activity', id: orderId },
                { type: 'Order', id: orderId },
              ],
      }),
      updateStages: builder.mutation<Order, { orderIds: number[]; stageId: number }>({
        query: ({ orderIds, stageId }) => {
          return {
            url: `${apiRoutes.orders}/bulkupdate/changestage`,
            method: 'PUT',
            data: { ids: orderIds, stageId },
          };
        },
        async onQueryStarted({ orderIds }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;

            orderIds.forEach((orderId) => {
              dispatch(
                ordersApiSlice.util.updateQueryData('getOrder', String(orderId), (order: Order) => {
                  if (order) {
                    order.stage = { ...data.stage };
                  }
                  return order;
                }),
              );
            });
          } catch (e) {
            console.log('unable to update stage', e);
          }
        },
        // invalidatesTags: ['Activities'],
        invalidatesTags: (_result, error, { orderIds }) =>
          error
            ? []
            : [
                ...orderIds.map((orderId) => {
                  return { type: 'Activity' as const, id: orderId };
                }),
                'Client',
                'UserActivities',
              ],
      }),
      updateStatus: builder.mutation<
        Order,
        {
          orderId: string;
          statusId: number;
          action: 'removestatus' | 'changestatus';
          scheduledNextActionDate: Date | null;
        }
      >({
        query: ({ orderId, statusId, action, scheduledNextActionDate }) => {
          const baseUrl =
            action === 'removestatus'
              ? `${apiRoutes.orders}/${orderId}/${action}`
              : `${apiRoutes.orders}/${orderId}/${action}/${statusId}`;

          const urlWithParams = scheduledNextActionDate
            ? `${baseUrl}?scheduledNextActionDate=${
                scheduledNextActionDate ? scheduledNextActionDate.toISOString().split('T')[0] : null
              }`
            : baseUrl;

          return {
            url: urlWithParams,
            method: 'PUT',
          };
        },
        async onQueryStarted({ orderId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;

            dispatch(
              ordersApiSlice.util.updateQueryData('getOrder', orderId, (order: Order) => {
                if (order) {
                  order.stage = { ...data.stage };
                }
                return order;
              }),
            );
          } catch (e) {
            console.log('unable to update status', e);
          }
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : [{ type: 'Activity', id: orderId }, 'Orders', 'Client'],
      }),
      updateStatuses: builder.mutation<Order, { orderIds: number[]; statusId: number }>({
        query: ({ orderIds, statusId }) => {
          return {
            url: `${apiRoutes.orders}/bulkupdate/setstatus`,
            method: 'PUT',
            data: { orderIds, statusId },
          };
        },
        async onQueryStarted({ orderIds }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;

            orderIds.forEach((orderId) => {
              dispatch(
                ordersApiSlice.util.updateQueryData('getOrder', String(orderId), (order: Order) => {
                  if (order) {
                    order.stage = { ...data.stage };
                  }
                  return order;
                }),
              );
            });
          } catch (e) {
            console.log('unable to update stage', e);
          }
        },
        invalidatesTags: (_result, error) => (error ? [] : ['Orders', 'Client']),
      }),
      // change status of order to cancelled
      cancelOrder: builder.mutation<
        undefined,
        { orderIds?: number[]; cancellationReasonId: number }
      >({
        query: ({ orderIds, cancellationReasonId }) => {
          if (orderIds) {
            return {
              url: `${apiRoutes.orders}/bulkupdate/changestage/cancel`,
              method: 'PUT',
              data: { orderIds, cancellationReasonId },
            };
          }
          throw new Error('Order ids are not defined');
        },
        async onQueryStarted(
          { orderIds, cancellationReasonId },
          { dispatch, queryFulfilled, getState },
        ) {
          try {
            await queryFulfilled;
            const orderCancellationReason = getState().api.queries[
              `getCancellationsForType(${CancellationType.Order})`
            ]?.data as any;
            const cancellations = orderCancellationReason.items;
            orderIds?.forEach((orderId) => {
              return dispatch(
                ordersApiSlice.util.updateQueryData('getOrder', String(orderId), (order: Order) => {
                  if (order) {
                    order.stage = { id: 6, name: 'Cancelled', status: null, changedAt: new Date() };
                    if (!order.cancellationReason) {
                      order['cancellationReason'] = {};
                      order['cancellationReason']['reason'] = cancellations.find(
                        (reason: any) => reason.id === cancellationReasonId,
                      )?.name;
                    }
                  }
                  return order;
                }),
              );
            });
          } catch (e) {
            console.error('Unable to cancel stage', e);
          }
        },
        invalidatesTags: (_result, error, { orderIds }) =>
          error || !orderIds
            ? []
            : [
                'Orders',
                'UserActivities',
                ...orderIds.map((orderId) => ({ type: 'Activity' as const, id: orderId })),
              ],
      }),
      // change status of order to uncancelled
      uncancelOrder: builder.mutation<undefined, { orderIds?: number[] }>({
        query: ({ orderIds }) => {
          if (orderIds) {
            return {
              url: `${apiRoutes.orders}/bulkupdate/changestage/uncancel`,
              method: 'PUT',
              data: { orderIds },
            };
          }
          throw new Error('Order ids are not defined');
        },
        async onQueryStarted({ orderIds }, { dispatch, queryFulfilled, getState }) {
          try {
            await queryFulfilled;
            const stagesAny = getState().api.queries['getStages(undefined)']?.data as any;
            const defaultStage = stagesAny.items.find((stage: any) => stage.isDefault);
            orderIds?.forEach((orderId) => {
              return dispatch(
                ordersApiSlice.util.updateQueryData('getOrder', String(orderId), (order: Order) => {
                  if (order) {
                    order.stage = {
                      id: defaultStage.id,
                      name: defaultStage.name,
                      status: null,
                      changedAt: new Date(),
                    };
                  }
                  return order;
                }),
              );
            });
          } catch (e) {
            console.error('Unable to uncancel stage', e);
          }
        },
        invalidatesTags: (_result, error, { orderIds }) =>
          error || !orderIds
            ? []
            : [
                'Orders',
                'UserActivities',
                ...orderIds.map((orderId) => ({ type: 'Activity' as const, id: orderId })),
              ],
      }),
      // activities
      getActivities: builder.query<Paged<Activity>, { orderId: string | number | undefined }>({
        query: ({ orderId }) => {
          if (orderId) {
            return {
              url: `${apiRoutes.orders}/${orderId}/activities`,
              method: 'GET',
            };
          }
          throw new Error('Order id is not defined');
        },
        providesTags: (_result, _error, { orderId }) => [{ type: 'Activity', id: orderId }],
      }),
      updateActivity: builder.mutation<
        Activity,
        { orderId: string | number; activityId: string; activity: EditActivity }
      >({
        query: ({ orderId, activityId, activity }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/activities/${activityId}`,
            method: 'PUT',
            data: activity,
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : ['Orders', 'UserActivities', { type: 'Activity', id: orderId }],
      }),
      deleteActivity: builder.mutation<Activity, { orderId: string | number; activityId: string }>({
        query: ({ activityId }) => {
          return {
            url: `${apiRoutes.activities}/${activityId}`,
            method: 'DELETE',
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : [{ type: 'Activity', id: orderId }],
      }),
      rescheduleActivity: builder.mutation<
        Activity,
        { orderId: string; activityId: string; activity: EditActivity }
      >({
        query: ({ orderId, activityId, activity }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/activities/${activityId}/reschedule`,
            method: 'PUT',
            data: activity,
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : ['Orders', 'UserActivities', { type: 'Activity', id: orderId }],
      }),
      updateActivities: builder.mutation<
        Activity[],
        { orderId: string; activities: EditActivity[] }
      >({
        query: ({ orderId, activities }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/activities/update`,
            method: 'PUT',
            data: activities,
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : ['Orders', 'UserActivities', { type: 'Activity', id: orderId }],
      }),
      // notes
      createNote: builder.mutation<NoteSchema, { orderId: string | number; note: NoteSchema }>({
        query: ({ orderId, note }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/activities`,
            method: 'POST',
            data: note,
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : [{ type: 'Activity', id: orderId }],
      }),
      assignSalesRep: builder.mutation<
        Order[],
        { orderIds: number[]; salesRepresentativeId: string }
      >({
        query: ({ orderIds, salesRepresentativeId }) => {
          return {
            url: `${apiRoutes.orders}/bulkupdate/assignsalesrepresentative`,
            method: 'PUT',
            data: { orderIds, salesRepresentativeId },
          };
        },
        invalidatesTags: (_result, error) => (error ? [] : ['Orders']),
      }),
      assignProcessor: builder.mutation<Order[], { orderIds: number[]; processorId: string }>({
        query: ({ orderIds, processorId }) => {
          return {
            url: `${apiRoutes.orders}/bulkupdate/assign-processor`,
            method: 'PUT',
            data: { orderIds, processorId },
          };
        },
        invalidatesTags: (_result, error, { orderIds }) =>
          error
            ? []
            : ['Orders', ...orderIds.map((orderId) => ({ type: 'Order' as const, id: orderId }))],
      }),
      exportSingleOrder: builder.mutation<undefined, { orderIds: number[] }>({
        query: ({ orderIds }) => {
          return {
            url: `${apiRoutes.orders}/export`,
            method: 'GET',
            headers: { 'X-Order-Ids': orderIds.toString() },
            responseType: 'blob',
          };
        },
      }),
      exportOrders: builder.mutation<
        Blob,
        {
          filters: MRT_ColumnFiltersState;
          sorting: MRT_SortingState;
        }
      >({
        query: ({ filters, sorting }) => {
          let filterData = [...filters];

          if (window.location.pathname === '/leads') {
            filterData = [{ id: 'stageId', value: '1' }, ...filters];
          }

          if (window.location.pathname === '/processing') {
            filterData = [{ id: 'stageId', value: '3' }, ...filters];
          }
          const urlParams = createMyUtiliesURL(filterData, sorting);

          return {
            url: `${apiRoutes.orders}/filterexport`,
            method: 'GET',
            responseType: 'blob',
            params: {
              ...Object.fromEntries(urlParams),
            },
          };
        },
      }),
      archiveOrder: builder.mutation<Order[], { orderIds: number[] }>({
        query: ({ orderIds }) => {
          return {
            url: `${apiRoutes.orders}/bulkupdate/changestage/archive`,
            method: 'PUT',
            data: { orderIds },
          };
        },
        invalidatesTags: (_result, error) => (error ? [] : ['Orders']),
      }),
      completeOrder: builder.mutation<Order[], { orderIds: number[] }>({
        query: ({ orderIds }) => {
          return {
            url: `${apiRoutes.orders}/bulkupdate/changestage/complete`,
            method: 'PUT',
            data: { orderIds },
          };
        },
        invalidatesTags: (result, error) => (error ? [] : ['Orders']),
      }),
      changeOrdersStageToSales: builder.mutation<Order[], { orderIds: number[] }>({
        query: ({ orderIds }) => {
          return {
            url: `${apiRoutes.orders}/bulkupdate/changestage/sales`,
            method: 'PUT',
            data: { orderIds },
          };
        },
        invalidatesTags: (result, error) => (error ? [] : ['Orders']),
      }),
      claimProcessor: builder.mutation<void, number>({
        query: (orderId) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/claim/processor`,
            method: 'PUT',
          };
        },
        invalidatesTags: (_result, error, orderId) =>
          error ? [] : ['Orders', { type: 'Order' as const, id: orderId }],
      }),
      claimSales: builder.mutation<void, number>({
        query: (orderId) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/claim/sales`,
            method: 'PUT',
          };
        },
        invalidatesTags: (_result, error, orderId) =>
          error ? [] : ['Orders', { type: 'Order' as const, id: orderId }],
      }),

      researchOrder: builder.mutation<void, { orderId: number; address?: SetAddressCandidate }>({
        query: ({ orderId, address }) => ({
          url: `/orders/${orderId}/reresearch`,
          method: 'POST',
          data: address,
        }),
        invalidatesTags: ['Order', 'Orders', 'Quotes', 'Subscriptions'],
      }),

      scheduleActivity: builder.mutation<void, { orderId: number; data: AddActivityDto }>({
        query: ({ orderId, data }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/activities`,
            method: 'POST',
            data,
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : ['Orders', 'UserActivities', { type: 'Activity', id: orderId }],
      }),

      getOrderVault: builder.query<VaultSchema, { orderId: string | undefined }>({
        query: ({ orderId }) => {
          if (orderId) {
            return { url: `${apiRoutes.orders}/${orderId}/vault`, method: 'GET' };
          }
          throw new Error('Order id is not defined');
        },
        providesTags: (_result, _error, { orderId }) => [{ type: 'Vault', id: orderId }],
      }),
      updateOrderVault: builder.mutation<void, { orderId: string | undefined; data: VaultSchema }>({
        query: ({ orderId, data }) => {
          if (orderId) {
            return {
              url: `${apiRoutes.orders}/${orderId}/vault`,
              method: 'PUT',
              data,
            };
          }
          throw new Error('Order id is not defined');
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : [{ type: 'Vault', id: orderId }],
      }),
      updateEsiId: builder.mutation<Order, string>({
        query: (orderId) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/esiid`,
            method: 'PUT',
          };
        },
        invalidatesTags: (_result, error, orderId) =>
          error ? [] : [{ type: 'Order', id: orderId }],
      }),
      updateEsiIdManually: builder.mutation<Order, { orderId: string | undefined; data: EsiId }>({
        query: ({ orderId, data }) => {
          if (orderId) {
            return {
              url: `${apiRoutes.orders}/${orderId}/esiidmanually`,
              method: 'PUT',
              data,
            };
          }
          throw new Error('Order id is not defined');
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : [{ type: 'Order', id: orderId }],
      }),
      sendOrderInformation: builder.mutation<Order, { orderId: string | undefined }>({
        query: ({ orderId }) => {
          if (orderId) {
            return {
              url: `${apiRoutes.orders}/${orderId}/sendemail`,
              method: 'POST',
            };
          }
          throw new Error('Order id is not defined');
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error
            ? []
            : [{ type: 'Order', id: orderId }, { type: 'Activity', id: orderId }, 'Activity'],
      }),
      muteOrder: builder.mutation<Order, string>({
        query: (orderId) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/mute`,
            method: 'PUT',
          };
        },
        invalidatesTags: (_result, error, orderId) =>
          error ? [] : [{ type: 'Order', id: orderId }, 'Orders'],
      }),
      getImportLeadsUploadUrl: builder.mutation<
        { key: string; url: string },
        { fileName: string; contentType?: string }
      >({
        query: ({ fileName, contentType = 'text/csv' }) => ({
          url: `${apiRoutes.orders}/importLeads/uploadUrl`,
          method: 'POST',
          data: { fileName, contentType },
        }),
      }),
      importLeadsCsv: builder.mutation<void, { userId?: string; s3key: string }>({
        query: ({ userId, s3key }) => ({
          url: `${apiRoutes.orders}/ImportLeadsCsvAsync`,
          method: 'POST',
          params: { s3key, userId },
        }),
        invalidatesTags: (_result, error) => (error ? [] : ['Orders']),
      }),
      sendOrderInsurance: builder.mutation<void, { orderId: number; data: InsurancePayload }>({
        query: ({ orderId, data }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/send-insurance`,
            method: 'POST',
            data,
          };
        },
      }),
      updateUrgency: builder.mutation<void, { orderId: number; isUrgent: boolean }>({
        query: ({ orderId, isUrgent }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/updateUrgency`,
            method: 'PUT',
            data: { isUrgent },
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : [{ type: 'Order', id: orderId }],
      }),
      updateProcessingNotes: builder.mutation<void, { orderId: number; processingNotes?: string }>({
        query: ({ orderId, processingNotes }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/updateProcessingNotes`,
            method: 'PUT',
            data: { processingNotes },
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : ['Orders', { type: 'Order', id: orderId }],
      }),
      addQuoteDetails: builder.mutation<
        any,
        { orderId: number; quoteId: number; details: QuoteDetails }
      >({
        query: ({ orderId, quoteId, details }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/quotes/${quoteId}/details`,
            method: 'POST',
            data: details,
          };
        },
        transformResponse: (_response: any, meta: string) => {
          return { id: meta };
        },
        invalidatesTags: (_result, error, { orderId, quoteId }) =>
          error
            ? []
            : [
                { type: 'Quotes', id: quoteId },
                { type: 'Order', id: orderId },
              ],
      }),
      dsiCreditCheck: builder.mutation<CreditCheckResponse, number>({
        query: (orderId) => {
          return {
            url: `${apiRoutes.dsi}/CreditCheck/${orderId}`,
            method: 'POST',
          };
        },
        invalidatesTags: (_result, error, orderId) =>
          error
            ? []
            : [
                { type: 'Orders', id: orderId },
                { type: 'Quotes', id: orderId },
                { type: 'Order', id: orderId },
              ],
      }),
      dsiOrderQualification: builder.mutation<EnrollResponse, number>({
        query: (quoteId) => {
          return {
            url: `${apiRoutes.dsi}/OrderQualification/${quoteId}`,
            method: 'POST',
          };
        },
        invalidatesTags: (_result, error, orderId) =>
          error
            ? []
            : [
                { type: 'Orders', id: orderId },
                { type: 'Quotes', id: orderId },
                { type: 'Order', id: orderId },
              ],
      }),
      dsiSubmitOrder: builder.mutation<OrderSubmitResponse, number>({
        query: (quoteId) => {
          return {
            url: `${apiRoutes.dsi}/SubmitOrder/${quoteId}`,
            method: 'POST',
          };
        },
        invalidatesTags: (_result, error, orderId) =>
          error
            ? []
            : [
                { type: 'Orders', id: orderId },
                { type: 'Quotes', id: orderId },
                { type: 'Order', id: orderId },
              ],
      }),
      dsiCreateOrder: builder.mutation<CreateOrderResponse, number>({
        query: (quoteId) => {
          return {
            url: `${apiRoutes.dsi}/CreateOrder/${quoteId}`,
            method: 'POST',
          };
        },
        invalidatesTags: (_result, error, quoteId) =>
          error
            ? []
            : [
                { type: 'Orders', id: quoteId },
                { type: 'Quotes', id: quoteId },
                { type: 'Order', id: quoteId },
              ],
      }),
      dsiSetAppointment: builder.mutation<
        CreateOrderResponse,
        { quoteId: number; appointment: AppointmentListItem }
      >({
        query: ({ quoteId, appointment }) => {
          return {
            url: `${apiRoutes.dsi}/SetAppointment/${quoteId}`,
            method: 'POST',
            data: {
              apptType: appointment.apptType,
              apptStartDateTime: appointment.startDate,
              apptEndDateTime: appointment.endDate,
              comment: 'test',
            },
          };
        },
        invalidatesTags: (_result, error, { quoteId: orderId }) =>
          error
            ? []
            : [
                { type: 'Quotes', id: orderId },
                { type: 'Order', id: orderId },
                { type: 'Orders', id: orderId },
              ],
      }),
      dsiGetCurrentStep: builder.query<string, number>({
        query: (quoteId) => {
          return {
            url: `${apiRoutes.dsi}/GetCurrentStep/${quoteId}`,
            method: 'GET',
          };
        },
        providesTags: (_result, _error, quoteId) => [{ type: 'Quote', id: quoteId }],
      }),
      dsiSetOrderVault: builder.mutation<void, { orderId: number; data: DsiVaultSchema }>({
        query: ({ orderId, data }) => {
          return {
            url: `${apiRoutes.dsi}/DsiVault/${orderId}`,
            method: 'POST',
            data,
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error
            ? []
            : [
                { type: 'Orders', id: orderId },
                { type: 'Quotes', id: orderId },
                { type: 'Order', id: orderId },
              ],
      }),
      dsiGetOrderVault: builder.query<DsiVaultSchema, number>({
        query: (orderId) => {
          return {
            url: `${apiRoutes.dsi}/DsiVault/${orderId}`,
            method: 'GET',
          };
        },
        providesTags: (_result, error, orderId) =>
          error
            ? []
            : [
                { type: 'Orders', id: orderId },
                // 'Quotes',
              ],
      }),
      dsiInfo: builder.query<DsiInfo, number>({
        query: (quoteId) => {
          return {
            url: `${apiRoutes.dsi}/${quoteId}/info`,
            method: 'GET',
          };
        },
        providesTags: (_result, error, orderId) =>
          error
            ? []
            : [
                { type: 'Orders', id: orderId },
                // 'Quotes',
              ],
      }),
      dsiRcPayment: builder.mutation<RcResponse, number>({
        query: (quoteId) => {
          return {
            url: `${apiRoutes.dsi}/RcPayment/${quoteId}`,
            method: 'POST',
          };
        },
        invalidatesTags: (_result, error, quoteId) =>
          error
            ? []
            : [
                { type: 'Orders', id: quoteId },
                { type: 'Quotes', id: quoteId },
                { type: 'Order', id: quoteId },
              ],
      }),
      updateAddress: builder.mutation<
        Address,
        { orderId: number; address: Omit<Address, 'coordinate'> }
      >({
        query: ({ orderId, address }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/address`,
            method: 'PUT',
            data: address,
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error
            ? []
            : [
                { type: 'Quotes', id: orderId },
                // 'Orders',
              ],
      }),
      toggleCallSchedulingEscalation: builder.mutation<
        void,
        { orderId: number; isEscalated: boolean }
      >({
        query: ({ orderId, isEscalated }) => {
          return {
            url: `${apiRoutes.orders}/${orderId}/ai-process-state/call-scheduling-escalation`,
            method: 'PATCH',
            data: { isEscalated },
          };
        },
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : [{ type: 'Order', id: orderId }, 'Orders'],
      }),
      dsiFinishOrder: builder.mutation<void, { quoteId: number; orderId: number }>({
        query: ({ quoteId }) => {
          return {
            url: `${apiRoutes.dsi}/Finish/${quoteId}`,
            method: 'POST',
          };
        },
        invalidatesTags: (_result, error, { quoteId, orderId }) =>
          error
            ? []
            : [
                { type: 'Orders', id: orderId },
                { type: 'Quotes', id: quoteId },
                { type: 'Order', id: orderId },
                { type: 'Subscriptions', id: orderId },
              ],
      }),
      dsiGetCreditCounter: builder.query<number, { quoteId: number; orderId: number }>({
        query: ({ quoteId }) => {
          return {
            url: `${apiRoutes.dsi}/CreditCheckCounter/${quoteId}`,
            method: 'GET',
          };
        },
      }),
      submitAddressCandidate: builder.mutation<
        SubmitAddressCandidateResponse,
        SubmitAddressCandidateRequest
      >({
        query: ({ orderId, address }) => ({
          url: '/spectrum/submit-candidate',
          method: 'POST',
          data: {
            orderId,
            address,
          },
        }),
        invalidatesTags: ['Order', 'Orders'],
      }),

      getAddressCandidates: builder.mutation<AddressCandidate[], GetAddressCandidatesRequest>({
        query: ({ selectedAddress }) => ({
          url: '/spectrum/address-candidates',
          method: 'POST',
          data: {
            selectedAddress,
          },
        }),
      }),

      setAddressConfirmation: builder.mutation<void, { orderId: number; isConfirmed: boolean }>({
        query: ({ orderId, isConfirmed }) => ({
          url: `${apiRoutes.orders}/${orderId}/address/confirmation`,
          method: 'PUT',
          data: { isConfirmed },
        }),
        invalidatesTags: (_result, error, { orderId }) =>
          error ? [] : [{ type: 'Order', id: orderId }, 'Orders'],
      }),
    };
  },

  overrideExisting: false,
});

export const {
  useGetLeadsOrdersQuery,
  useGetOrdersQuery,
  useGetOrderQuery,
  useCreateOrderAndClientMutation,
  useUpdateOrderMutation,
  useGetOrderTicketsQuery,
  useCreateTicketMutation,
  useUpdateTicketMutation,
  useGetOrderAllQuotesQuery,
  useLazyGetOrderAllQuotesQuery,
  useAddOrderQuoteMutation,
  useRemoveOrderQuoteMutation,
  useUpdateOrderQuoteMutation,
  useGetOrderSubscriptionsQuery,
  useAddOrderSubscriptionMutation,
  useDeleteOrderSubscriptionMutation,
  useUpdateOrderSubscriptionMutation,
  useUpdateStageMutation,
  useUpdateStagesMutation,
  useUpdateStatusMutation,
  useGetActivitiesQuery,
  useUpdateActivityMutation,
  useUpdateActivitiesMutation,
  useDeleteActivityMutation,
  useScheduleActivityMutation,
  useCreateNoteMutation,
  useAssignSalesRepMutation,
  useCancelOrderMutation,
  useUncancelOrderMutation,
  useExportSingleOrderMutation,
  useExportOrdersMutation,
  useArchiveOrderMutation,
  useClaimProcessorMutation,
  useClaimSalesMutation,
  useUpdateStatusesMutation,
  useCompleteOrderMutation,
  useGetOrderVaultQuery,
  useLazyGetOrderVaultQuery,
  useUpdateOrderVaultMutation,
  useUpdateEsiIdMutation,
  useUpdateEsiIdManuallyMutation,
  useSendOrderInformationMutation,
  useMuteOrderMutation,
  useAssignProcessorMutation,
  useChangeOrdersStageToSalesMutation,
  useGetImportLeadsUploadUrlMutation,
  useImportLeadsCsvMutation,
  useResearchOrderMutation,
  useLazyGetOrderQuery,
  useSendOrderInsuranceMutation,
  useRescheduleActivityMutation,
  useUpdateUrgencyMutation,
  useUpdateProcessingNotesMutation,
  useAddQuoteDetailsMutation,
  useDsiSubmitOrderMutation,
  useDsiCreditCheckMutation,
  useDsiOrderQualificationMutation,
  useDsiCreateOrderMutation,
  useDsiSetAppointmentMutation,
  useUpdateQuoteStepMutation,
  useDsiGetCurrentStepQuery,
  useLazyDsiGetCurrentStepQuery,
  useDsiSetOrderVaultMutation,
  useDsiGetOrderVaultQuery,
  useLazyDsiGetOrderVaultQuery,
  useDsiRcPaymentMutation,
  useUpdateAddressMutation,
  useDsiFinishOrderMutation,
  useDsiInfoQuery,
  useDsiGetCreditCounterQuery,
  useSubmitAddressCandidateMutation,
  useGetAddressCandidatesMutation,
  useToggleCallSchedulingEscalationMutation,
  useSetAddressConfirmationMutation,
} = ordersApiSlice;
